/* eslint-disable @wyz/module/no-relative-import */
import { IPaginationResponse } from '@wyz/types';
import { FullPageSpinner } from '@wyz/ui';
import {
  callApi,
  DateUtils,
  LocaleUtils,
  MoneyUtils,
  NumberHelpers,
  useQuery,
} from '@wyz/utils';
import { orderBy } from 'lodash-es';
import * as React from 'react';
import { useAuth } from 'react-oidc-context';
import { useSearchParams } from 'react-router';
import {
  ILocaleContext,
  ITenantLanguage,
  ITenantLanguageResponse,
} from '~/common/types';
import { ConnectQueryKeys } from '~/connect/api';
import { useConfig } from './config-provider';
import { LocaleContext } from './contexts';

export function LocaleProvider(props: React.PropsWithChildren) {
  const auth = useAuth();
  const { tenantCode, apiUrl } = useConfig();
  const [params] = useSearchParams();

  const locale =
    params.get('locale') ||
    auth?.user?.profile.locale ||
    LocaleUtils.DEFAULT_LOCALE;

  const language =
    locale.split('-').at(0) ?? LocaleUtils.DEFAULT_LOCALE.split('-')[0];
  const country =
    locale.split('-').at(1) ?? LocaleUtils.DEFAULT_LOCALE.split('-')[1];

  React.useLayoutEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const formatDate: ILocaleContext['formatters']['date'] = React.useCallback(
    (date, options) =>
      DateUtils.formatTimestamp(date, {
        ...options,
        locale: locale,
      }),
    [locale],
  );
  const formatMoney: ILocaleContext['formatters']['money'] = React.useCallback(
    (price, options) =>
      MoneyUtils.formatPrice(price, {
        ...options,
        locale: locale,
      }),
    [locale],
  );
  const formatQuantity: ILocaleContext['formatters']['quantity'] =
    React.useCallback(
      (value, options) =>
        NumberHelpers.formatQuantity(value, {
          ...options,
        }),
      [],
    );
  const getCurrencySymbol = React.useCallback(
    (currency: string) => MoneyUtils.getCurrencySymbol(currency, locale),
    [locale],
  );
  const call = callApi({
    baseUrl: apiUrl,
    token: auth.user?.access_token,
  });

  const getTenantLanguagesQuery = useQuery({
    queryKey: ConnectQueryKeys.languages.activeByTenant(),
    queryFn: () =>
      call<IPaginationResponse<ITenantLanguage>>(
        `/tenants/api/wtb/tenants/${tenantCode}/languages`,
        {},
      ).then((response) => {
        const asArray: Array<ITenantLanguage> = [];
        const byIsoCode = response.data.reduce(
          (acc, language) => {
            const lang: ITenantLanguage = {
              ...language,
              humanName:
                LocaleUtils.getHumanReadableLabel({
                  userLocale: language.isoCode,
                  locale: language.isoCode,
                  fallback: language.name,
                  options: { type: 'language' },
                }) ?? language.name,
            };
            acc[language.isoCode] = lang;
            asArray.push(lang);
            return acc;
          },
          {} as ITenantLanguageResponse['byIsoCode'],
        );
        return {
          byIsoCode,
          byIndex: orderBy(
            asArray,
            (language) => language.humanName.toLowerCase(),
            ['asc'],
          ),
        } satisfies ITenantLanguageResponse;
      }),
    retry: false,
    refetchOnMount: true,
  });
  const context: ILocaleContext = React.useMemo(() => {
    return {
      locale: locale,
      resolvedLocale:
        getTenantLanguagesQuery.data?.byIsoCode[locale]?.resolvedIsoCode ||
        locale,
      languages: getTenantLanguagesQuery.data || {
        byIsoCode: {},
        byIndex: [],
      },
      language,
      country,
      formatters: {
        date: formatDate,
        money: formatMoney,
        currency: getCurrencySymbol,
        quantity: formatQuantity,
      },
    } satisfies ILocaleContext;
  }, [
    locale,
    getTenantLanguagesQuery.data,
    language,
    country,
    formatDate,
    formatMoney,
    getCurrencySymbol,
    formatQuantity,
  ]);

  if (!getTenantLanguagesQuery.isSettled) {
    return <FullPageSpinner />;
  }

  return (
    <LocaleContext.Provider value={context}>
      {props.children}
    </LocaleContext.Provider>
  );
}

export const useLocale = (): ILocaleContext => {
  const context = React.useContext(LocaleContext);
  if (context === undefined) {
    throw new Error('useLocale must be used within a LocaleContext');
  }
  return context;
};
